/**
 * Coffee Bean Clicker v2 - Game Actions
 * All state mutations go through actions
 */

import type { ActionHandler } from '@app-games/core';
import type { GameState } from './state.js';
import {
  computeTapPower, computeBPS, computePrestigeGoldenBeans, computeAscensionStarDust,
  computeCurrentStage, getCritChance, getCritMultiplier, getComboMultiplier,
  getTapUpgradeCost, getProducerCost, applyCostDiscount,
  canPrestige, canAscend, isProducerUnlocked,
} from './state.js';
import {
  TAP_UPGRADES, PRODUCERS, PRESTIGE_UPGRADES, STAR_UPGRADES, VARIETIES, ROAST_ZONES,
} from './data/upgrades.js';
import { ACHIEVEMENTS } from './data/achievements.js';

// ═══════════════════════════════════════════════
//  Action Types
// ═══════════════════════════════════════════════

export type GameAction =
  | { type: 'TAP' }
  | { type: 'BUY_TAP_UPGRADE'; payload: { upgradeId: string } }
  | { type: 'BUY_PRODUCER'; payload: { producerId: string } }
  | { type: 'BUY_PRESTIGE_UPGRADE'; payload: { upgradeId: string } }
  | { type: 'BUY_STAR_UPGRADE'; payload: { upgradeId: string } }
  | { type: 'PRESTIGE'; payload?: { bonusMultiplier?: number } }
  | { type: 'ASCEND' }
  | { type: 'COLLECT_OFFLINE'; payload: { beans: number; seconds: number } }
  | { type: 'ACTIVATE_BOOST'; payload: { target: 'bps' | 'tap'; multiplier: number; durationSec: number } }
  | { type: 'TAP_GOLDEN_CHERRY'; payload?: { bonusMultiplier?: number } }
  | { type: 'SPAWN_GOLDEN_CHERRY' }
  | { type: 'EXPIRE_GOLDEN_CHERRY' }
  | { type: 'START_ROASTING' }
  | { type: 'STOP_ROASTING' }
  | { type: 'COLLECT_ROAST'; payload?: { bonusMultiplier?: number } }
  | { type: 'CHECK_ACHIEVEMENTS' }
  | { type: 'CHECK_VARIETIES' }
  | { type: 'CHECK_STAGE' }
  | { type: 'ADD_BEANS'; payload: { amount: number } }
  | { type: 'RECORD_AD_WATCHED' }
  | { type: 'SET_RUNNING'; payload: { running: boolean } }
  | { type: 'UPDATE_SETTINGS'; payload: Partial<GameState['settings']> }
  | { type: 'UPDATE_LOGIN_STREAK'; payload: { today: string } }
  | { type: 'RESET' };

// ═══════════════════════════════════════════════
//  Handlers
// ═══════════════════════════════════════════════

const handleTap: ActionHandler<GameState, Extract<GameAction, { type: 'TAP' }>> = (state) => {
  if (!state.isRunning) return null;

  const basePower = computeTapPower(state);
  const critChance = getCritChance(state) / 100;
  const isCritical = Math.random() < critChance;
  const critMul = isCritical ? getCritMultiplier(state) : 1;

  // Combo
  const newCombo = state.combo.count + 1;
  const comboMul = getComboMultiplier(newCombo);

  const earned = Math.floor(basePower * critMul * comboMul);

  return {
    ...state,
    beans: state.beans + earned,
    combo: {
      ...state.combo,
      count: newCombo,
      timer: state.combo.maxTimer,
    },
    stats: {
      ...state.stats,
      totalBeans: state.stats.totalBeans + earned,
      totalBeansThisRun: state.stats.totalBeansThisRun + earned,
      totalTaps: state.stats.totalTaps + 1,
      totalCritTaps: state.stats.totalCritTaps + (isCritical ? 1 : 0),
      maxCombo: Math.max(state.stats.maxCombo, newCombo),
    },
  };
};

const handleBuyTapUpgrade: ActionHandler<
  GameState, Extract<GameAction, { type: 'BUY_TAP_UPGRADE' }>
> = (state, action) => {
  const def = TAP_UPGRADES.find(t => t.id === action.payload.upgradeId);
  if (!def) return null;
  const lvl = state.tapUpgrades[def.id] ?? 0;
  if (lvl >= def.maxLevel) return null;
  const cost = applyCostDiscount(state, getTapUpgradeCost(def, lvl));
  if (state.beans < cost) return null;

  return {
    ...state,
    beans: state.beans - cost,
    tapUpgrades: { ...state.tapUpgrades, [def.id]: lvl + 1 },
    stats: { ...state.stats, totalUpgradesBought: state.stats.totalUpgradesBought + 1 },
  };
};

const handleBuyProducer: ActionHandler<
  GameState, Extract<GameAction, { type: 'BUY_PRODUCER' }>
> = (state, action) => {
  const def = PRODUCERS.find(p => p.id === action.payload.producerId);
  if (!def || !isProducerUnlocked(def, state)) return null;
  const owned = state.producers[def.id] ?? 0;
  if (owned >= def.maxCount) return null;
  const cost = applyCostDiscount(state, getProducerCost(def, owned), def.id);
  if (state.beans < cost) return null;

  return {
    ...state,
    beans: state.beans - cost,
    producers: { ...state.producers, [def.id]: owned + 1 },
    stats: { ...state.stats, totalUpgradesBought: state.stats.totalUpgradesBought + 1 },
  };
};

const handleBuyPrestigeUpgrade: ActionHandler<
  GameState, Extract<GameAction, { type: 'BUY_PRESTIGE_UPGRADE' }>
> = (state, action) => {
  const def = PRESTIGE_UPGRADES.find(p => p.id === action.payload.upgradeId);
  if (!def) return null;
  const cur = state.prestigeUpgrades[def.id] ?? 0;
  if (cur >= def.maxLevel || state.goldenBeans < def.cost) return null;

  return {
    ...state,
    goldenBeans: state.goldenBeans - def.cost,
    prestigeUpgrades: { ...state.prestigeUpgrades, [def.id]: cur + 1 },
  };
};

const handleBuyStarUpgrade: ActionHandler<
  GameState, Extract<GameAction, { type: 'BUY_STAR_UPGRADE' }>
> = (state, action) => {
  const def = STAR_UPGRADES.find(s => s.id === action.payload.upgradeId);
  if (!def) return null;
  const cur = state.starUpgrades[def.id] ?? 0;
  if (cur >= def.maxLevel || state.starDust < def.cost) return null;

  const newState = {
    ...state,
    starDust: state.starDust - def.cost,
    starUpgrades: { ...state.starUpgrades, [def.id]: cur + 1 },
  };

  // S5: Combo decay slower (3s)
  if (def.id === 'S5') {
    newState.combo = { ...newState.combo, maxTimer: 3 };
  }

  return newState;
};

const handlePrestige: ActionHandler<
  GameState, Extract<GameAction, { type: 'PRESTIGE' }>
> = (state, action) => {
  if (!canPrestige(state)) return null;

  let earnedGB = computePrestigeGoldenBeans(state);
  if (action.payload?.bonusMultiplier) {
    earnedGB = Math.floor(earnedGB * action.payload.bonusMultiplier);
  }

  const runTime = state.playTime - state.stats.prestigeStartTime;

  const preserved = {
    prestigeUpgrades: { ...state.prestigeUpgrades },
    starUpgrades: { ...state.starUpgrades },
    achievements: [...state.unlockedAchievements],
    varieties: [...state.unlockedVarieties],
    settings: { ...state.settings },
  };

  const tapUpgrades: Record<string, number> = {};
  for (const t of TAP_UPGRADES) tapUpgrades[t.id] = 0;
  const producers: Record<string, number> = {};
  for (const p of PRODUCERS) producers[p.id] = 0;

  // G9: start with 10x P1~P3
  if (preserved.prestigeUpgrades['G9']) {
    producers['P1'] = 10; producers['P2'] = 10; producers['P3'] = 10;
  }

  // S1: starting beans 1M
  const startBeans = state.starUpgrades['S1'] ? 1_000_000 : 0;

  // G14: start at stage 5
  const startStage = preserved.prestigeUpgrades['G14'] ? 5 : 1;

  const comboMaxTimer = state.starUpgrades['S5'] ? 3 : 1.5;

  return {
    ...state,
    beans: startBeans,
    goldenBeans: state.goldenBeans + earnedGB,
    tapUpgrades,
    producers,
    prestigeUpgrades: preserved.prestigeUpgrades,
    starUpgrades: preserved.starUpgrades,
    unlockedAchievements: preserved.achievements,
    unlockedVarieties: preserved.varieties,
    combo: { count: 0, timer: 0, maxTimer: comboMaxTimer },
    boosts: { bpsMultiplier: 1, bpsEndTime: 0, tapMultiplier: 1, tapEndTime: 0 },
    goldenCherry: { active: false, nextSpawnTime: state.playTime + 60, endTime: 0 },
    roasting: { active: false, heatLevel: 0, speed: 0.4, result: null },
    currentStage: startStage,
    stats: {
      ...state.stats,
      totalBeansThisRun: 0,
      totalPrestiges: state.stats.totalPrestiges + 1,
      totalGoldenBeansEarned: state.stats.totalGoldenBeansEarned + earnedGB,
      maxPrestigeGB: Math.max(state.stats.maxPrestigeGB, earnedGB),
      fastestPrestige: state.stats.fastestPrestige === 0
        ? runTime
        : Math.min(state.stats.fastestPrestige, runTime),
      prestigeStartTime: state.playTime,
    },
    settings: preserved.settings,
  };
};

const handleAscend: ActionHandler<
  GameState, Extract<GameAction, { type: 'ASCEND' }>
> = (state) => {
  if (!canAscend(state)) return null;

  const earnedSD = computeAscensionStarDust(state);
  if (earnedSD <= 0) return null;

  const preserved = {
    starUpgrades: { ...state.starUpgrades },
    achievements: [...state.unlockedAchievements],
    varieties: [...state.unlockedVarieties],
    settings: { ...state.settings },
  };

  // Reset everything including golden bean upgrades
  const tapUpgrades: Record<string, number> = {};
  for (const t of TAP_UPGRADES) tapUpgrades[t.id] = 0;
  const producers: Record<string, number> = {};
  for (const p of PRODUCERS) producers[p.id] = 0;
  const prestigeUpgrades: Record<string, number> = {};
  for (const p of PRESTIGE_UPGRADES) prestigeUpgrades[p.id] = 0;

  const comboMaxTimer = preserved.starUpgrades['S5'] ? 3 : 1.5;

  return {
    ...state,
    beans: 0,
    goldenBeans: 0,
    starDust: state.starDust + earnedSD,
    tapUpgrades,
    producers,
    prestigeUpgrades,
    starUpgrades: preserved.starUpgrades,
    unlockedAchievements: preserved.achievements,
    unlockedVarieties: preserved.varieties,
    combo: { count: 0, timer: 0, maxTimer: comboMaxTimer },
    boosts: { bpsMultiplier: 1, bpsEndTime: 0, tapMultiplier: 1, tapEndTime: 0 },
    goldenCherry: { active: false, nextSpawnTime: state.playTime + 60, endTime: 0 },
    roasting: { active: false, heatLevel: 0, speed: 0.4, result: null },
    currentStage: 1,
    stats: {
      ...state.stats,
      totalBeansThisRun: 0,
      totalAscensions: state.stats.totalAscensions + 1,
      prestigeStartTime: state.playTime,
    },
    settings: preserved.settings,
  };
};

const handleCollectOffline: ActionHandler<
  GameState, Extract<GameAction, { type: 'COLLECT_OFFLINE' }>
> = (state, action) => {
  const { beans, seconds } = action.payload;
  if (beans <= 0) return null;
  return {
    ...state,
    beans: state.beans + beans,
    stats: {
      ...state.stats,
      totalBeans: state.stats.totalBeans + beans,
      totalBeansThisRun: state.stats.totalBeansThisRun + beans,
      maxOfflineSeconds: Math.max(state.stats.maxOfflineSeconds, seconds),
    },
  };
};

const handleActivateBoost: ActionHandler<
  GameState, Extract<GameAction, { type: 'ACTIVATE_BOOST' }>
> = (state, action) => {
  const { target, multiplier, durationSec } = action.payload;
  const endTime = state.playTime + durationSec;
  if (target === 'bps') {
    return { ...state, boosts: { ...state.boosts, bpsMultiplier: multiplier, bpsEndTime: endTime } };
  }
  return { ...state, boosts: { ...state.boosts, tapMultiplier: multiplier, tapEndTime: endTime } };
};

const handleTapGoldenCherry: ActionHandler<
  GameState, Extract<GameAction, { type: 'TAP_GOLDEN_CHERRY' }>
> = (state, _action) => {
  if (!state.goldenCherry.active) return null;

  // Start roasting minigame instead of instant reward
  const nextSpawn = state.playTime + 120 + Math.random() * 180;

  return {
    ...state,
    goldenCherry: { active: false, nextSpawnTime: nextSpawn, endTime: 0 },
    roasting: { active: true, heatLevel: 0, speed: 0.3 + Math.random() * 0.2, result: null },
    stats: { ...state.stats, goldenCherryTaps: state.stats.goldenCherryTaps + 1 },
  };
};

const handleSpawnGoldenCherry: ActionHandler<
  GameState, Extract<GameAction, { type: 'SPAWN_GOLDEN_CHERRY' }>
> = (state) => ({
  ...state,
  goldenCherry: { ...state.goldenCherry, active: true, endTime: state.playTime + 10 },
});

const handleExpireGoldenCherry: ActionHandler<
  GameState, Extract<GameAction, { type: 'EXPIRE_GOLDEN_CHERRY' }>
> = (state) => {
  if (!state.goldenCherry.active) return null;
  const next = state.playTime + 120 + Math.random() * 180;
  return { ...state, goldenCherry: { active: false, nextSpawnTime: next, endTime: 0 } };
};

const handleStartRoasting: ActionHandler<
  GameState, Extract<GameAction, { type: 'START_ROASTING' }>
> = (state) => {
  if (state.roasting.active) return null;
  return {
    ...state,
    roasting: { active: true, heatLevel: 0, speed: 0.3 + Math.random() * 0.2, result: null },
  };
};

const handleStopRoasting: ActionHandler<
  GameState, Extract<GameAction, { type: 'STOP_ROASTING' }>
> = (state) => {
  if (!state.roasting.active || state.roasting.result !== null) return null;

  const heat = state.roasting.heatLevel;
  let multiplier = 1;
  let isPerfect = false;

  // Check roast zones (perfect first)
  for (const zone of ROAST_ZONES) {
    if (heat >= zone.min && heat < zone.max) {
      multiplier = zone.multiplier;
      isPerfect = !!zone.isPerfect;
      break;
    }
  }

  return {
    ...state,
    roasting: { ...state.roasting, result: multiplier },
    stats: {
      ...state.stats,
      perfectRoasts: state.stats.perfectRoasts + (isPerfect ? 1 : 0),
    },
  };
};

const handleCollectRoast: ActionHandler<
  GameState, Extract<GameAction, { type: 'COLLECT_ROAST' }>
> = (state, action) => {
  if (!state.roasting.active || state.roasting.result === null) return null;

  const bps = computeBPS(state);
  let bonus = Math.floor(bps * 30 * state.roasting.result);
  if (bonus < 10) bonus = 10;

  const adMul = action.payload?.bonusMultiplier ?? 1;
  bonus = Math.floor(bonus * adMul);

  return {
    ...state,
    beans: state.beans + bonus,
    roasting: { active: false, heatLevel: 0, speed: 0.4, result: null },
    stats: {
      ...state.stats,
      totalBeans: state.stats.totalBeans + bonus,
      totalBeansThisRun: state.stats.totalBeansThisRun + bonus,
    },
  };
};

const handleCheckAchievements: ActionHandler<
  GameState, Extract<GameAction, { type: 'CHECK_ACHIEVEMENTS' }>
> = (state) => {
  const newAch: string[] = [];
  for (const ach of ACHIEVEMENTS) {
    if (state.unlockedAchievements.includes(ach.id)) continue;
    if (ach.check(state)) newAch.push(ach.id);
  }
  if (newAch.length === 0) return null;

  let bonusGB = 0;
  for (const id of newAch) {
    const ach = ACHIEVEMENTS.find(a => a.id === id);
    if (ach?.reward.type === 'goldenBeanFlat') bonusGB += ach.reward.amount;
  }

  return {
    ...state,
    unlockedAchievements: [...state.unlockedAchievements, ...newAch],
    goldenBeans: state.goldenBeans + bonusGB,
  };
};

const handleCheckVarieties: ActionHandler<
  GameState, Extract<GameAction, { type: 'CHECK_VARIETIES' }>
> = (state) => {
  const newV: string[] = [];
  for (const v of VARIETIES) {
    if (state.unlockedVarieties.includes(v.id)) continue;
    let unlocked = false;
    if (v.unlockType === 'beans') unlocked = state.stats.totalBeans >= v.unlockValue;
    else if (v.unlockType === 'prestige') unlocked = state.stats.totalPrestiges >= v.unlockValue;
    else if (v.unlockType === 'achievements') unlocked = state.unlockedAchievements.length >= v.unlockValue;
    if (unlocked) newV.push(v.id);
  }
  if (newV.length === 0) return null;
  return { ...state, unlockedVarieties: [...state.unlockedVarieties, ...newV] };
};

const handleCheckStage: ActionHandler<
  GameState, Extract<GameAction, { type: 'CHECK_STAGE' }>
> = (state) => {
  const newStage = computeCurrentStage(state.stats.totalBeans);
  if (newStage === state.currentStage) return null;
  return { ...state, currentStage: newStage };
};

const handleAddBeans: ActionHandler<
  GameState, Extract<GameAction, { type: 'ADD_BEANS' }>
> = (state, action) => {
  if (!state.isRunning || action.payload.amount <= 0) return null;
  return {
    ...state,
    beans: state.beans + action.payload.amount,
    stats: {
      ...state.stats,
      totalBeans: state.stats.totalBeans + action.payload.amount,
      totalBeansThisRun: state.stats.totalBeansThisRun + action.payload.amount,
    },
  };
};

const handleRecordAdWatched: ActionHandler<
  GameState, Extract<GameAction, { type: 'RECORD_AD_WATCHED' }>
> = (state) => ({
  ...state,
  stats: { ...state.stats, totalAdsWatched: state.stats.totalAdsWatched + 1 },
});

const handleSetRunning: ActionHandler<
  GameState, Extract<GameAction, { type: 'SET_RUNNING' }>
> = (state, action) => {
  if (state.isRunning === action.payload.running) return null;
  return { ...state, isRunning: action.payload.running };
};

const handleUpdateSettings: ActionHandler<
  GameState, Extract<GameAction, { type: 'UPDATE_SETTINGS' }>
> = (state, action) => ({
  ...state,
  settings: { ...state.settings, ...action.payload },
});

const handleUpdateLoginStreak: ActionHandler<
  GameState, Extract<GameAction, { type: 'UPDATE_LOGIN_STREAK' }>
> = (state, action) => {
  const { today } = action.payload;
  if (today === state.stats.lastLoginDate) return null;
  const todayDate = new Date(today);
  const lastDate = state.stats.lastLoginDate ? new Date(state.stats.lastLoginDate) : null;
  const isConsecutive = lastDate && (todayDate.getTime() - lastDate.getTime()) <= 2 * 24 * 3600 * 1000;

  return {
    ...state,
    stats: {
      ...state.stats,
      lastLoginDate: today,
      consecutiveLoginDays: isConsecutive ? state.stats.consecutiveLoginDays + 1 : 1,
    },
  };
};

const handleReset: ActionHandler<GameState, Extract<GameAction, { type: 'RESET' }>> = () => null;

// ═══════════════════════════════════════════════
//  Handler Map
// ═══════════════════════════════════════════════

export const actionHandlers = {
  TAP: handleTap,
  BUY_TAP_UPGRADE: handleBuyTapUpgrade,
  BUY_PRODUCER: handleBuyProducer,
  BUY_PRESTIGE_UPGRADE: handleBuyPrestigeUpgrade,
  BUY_STAR_UPGRADE: handleBuyStarUpgrade,
  PRESTIGE: handlePrestige,
  ASCEND: handleAscend,
  COLLECT_OFFLINE: handleCollectOffline,
  ACTIVATE_BOOST: handleActivateBoost,
  TAP_GOLDEN_CHERRY: handleTapGoldenCherry,
  SPAWN_GOLDEN_CHERRY: handleSpawnGoldenCherry,
  EXPIRE_GOLDEN_CHERRY: handleExpireGoldenCherry,
  START_ROASTING: handleStartRoasting,
  STOP_ROASTING: handleStopRoasting,
  COLLECT_ROAST: handleCollectRoast,
  CHECK_ACHIEVEMENTS: handleCheckAchievements,
  CHECK_VARIETIES: handleCheckVarieties,
  CHECK_STAGE: handleCheckStage,
  ADD_BEANS: handleAddBeans,
  RECORD_AD_WATCHED: handleRecordAdWatched,
  SET_RUNNING: handleSetRunning,
  UPDATE_SETTINGS: handleUpdateSettings,
  UPDATE_LOGIN_STREAK: handleUpdateLoginStreak,
  RESET: handleReset,
} as const;
